import { useFormik, FormikContext, Form, Field, FormikHelpers } from 'formik';
import clsx from 'clsx';

import { TextField } from 'formik-material-ui';
import ButtonBase from '@material-ui/core/ButtonBase';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import ArrowForward from '@material-ui/icons/ArrowForward';
import ArrowBack from '@material-ui/icons/ArrowBack';

import {
  BuildingIcon,
  BusIcon,
  TaxiIcon,
  CarIcon,
} from '../../../assets/icons';
import useStyles from '../styles';
import { useStepperContext } from '../hooks/useStepper';
import { ThirdStepSchema, VaccineValues } from '../validation';
import { useCallback, useMemo } from 'react';
import { TransportType } from '../../../services/models/vaccination';

function VaccineThirdStep() {
  const classes = useStyles();
  const { commit, handlePrev, handleNext, data } =
    useStepperContext<VaccineValues>();

  const handleSubmit = useCallback(
    (formData: Partial<VaccineValues>) => {
      commit(formData);
      handleNext();
    },
    [commit, handleNext]
  );

  const formik = useFormik({
    validationSchema: ThirdStepSchema,
    initialValues: {
      transportType: data?.transportType,
      vehicleId: data?.vehicleId ?? '',
    },
    onSubmit: handleSubmit,
  });

  const handleVehicleChange = useCallback(
    async (
      type: TransportType,
      setFieldValue: FormikHelpers<any>['setFieldValue']
    ) => {
      setFieldValue('transportType', type);
      setFieldValue('vehicleId', '');
    },
    []
  );

  const { setFieldValue, values, initialValues } = formik;

  const isDisabled = useMemo(() => {
    if (values === initialValues && !values.transportType) {
      return true;
    }

    if (!values.transportType) {
      return true;
    }

    if (values.transportType === TransportType.OwnVehicle) {
      if (!values.vehicleId) {
        return true;
      }

      if (values.vehicleId.length < 7) {
        return true;
      }

      return false;
    }

    return false;
  }, [values, initialValues]);

  return (
    <FormikContext.Provider value={formik}>
      <Form>
        <Grid container direction="column">
          <Grid item>
            <Box px={2} className={classes.disclaimer}>
              <img src={BuildingIcon} height={110} alt="building-icon" />
              <Typography variant="h1">Como você vai vir?</Typography>
              <Typography>Escolha o meio de transporte</Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box display="flex" justifyContent="center">
              <Box maxWidth="500px">
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <ButtonBase
                      onClick={() =>
                        handleVehicleChange(
                          TransportType.PublicTransportation,
                          setFieldValue
                        )
                      }
                      classes={{
                        root: clsx(
                          classes.vehicleCard,
                          values.transportType ===
                            TransportType.PublicTransportation && 'selected'
                        ),
                      }}
                    >
                      <Grid container direction="column" spacing={2}>
                        <Grid item>
                          <img src={BusIcon} alt="bus-icon" />
                        </Grid>
                        <Grid item>Transporte Público</Grid>
                      </Grid>
                    </ButtonBase>
                  </Grid>
                  <Grid item xs={6}>
                    <ButtonBase
                      onClick={() =>
                        handleVehicleChange(
                          TransportType.AppOrTaxi,
                          setFieldValue
                        )
                      }
                      classes={{
                        root: clsx(
                          classes.vehicleCard,
                          values.transportType === TransportType.AppOrTaxi &&
                            'selected'
                        ),
                      }}
                    >
                      <Grid container direction="column" spacing={2}>
                        <Grid item>
                          <img src={TaxiIcon} alt="taxi-icon" />
                        </Grid>
                        <Grid item>Aplicativo ou Táxi</Grid>
                      </Grid>
                    </ButtonBase>
                  </Grid>
                  <Grid item xs={12}>
                    <ButtonBase
                      onClick={() =>
                        handleVehicleChange(
                          TransportType.OwnVehicle,
                          setFieldValue
                        )
                      }
                      classes={{
                        root: clsx(
                          classes.vehicleCard,
                          values.transportType === TransportType.OwnVehicle &&
                            'selected'
                        ),
                      }}
                    >
                      <Grid
                        container
                        justify="space-between"
                        alignItems="center"
                      >
                        <Grid item>Condução Própria</Grid>
                        <Grid item>
                          <img src={CarIcon} alt="bus-icon" />
                        </Grid>
                      </Grid>
                    </ButtonBase>
                    {values.transportType === TransportType.OwnVehicle && (
                      <Field
                        component={TextField}
                        id="vehicleId"
                        name="vehicleId"
                        label="Placa"
                        placeholder="AAA0A00"
                        size="small"
                        margin="normal"
                        inputProps={{
                          maxLength: 7,
                        }}
                        InputProps={{
                          classes: {
                            root: classes.input,
                          },
                        }}
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>
          <Grid item>
            <Box textAlign="center" py={3}>
              <Button
                startIcon={<ArrowBack />}
                variant="text"
                color="primary"
                disableElevation
                className={classes.button}
                onClick={() => handlePrev()}
              >
                Voltar
              </Button>{' '}
              <Button
                endIcon={<ArrowForward />}
                variant="contained"
                color="primary"
                disableElevation
                type="submit"
                className={classes.button}
                disabled={isDisabled}
              >
                Confirmar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Form>
    </FormikContext.Provider>
  );
}

export default VaccineThirdStep;
